import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import { BUSINESS_TOOL_SUB_SECTIONS } from 'constants/businessTool';
import { FOR_INVESTORS_SECTIONS } from 'constants/businessToolSections/forInvestors';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';
import { FinancialPlanChipSelector } from 'components/BusinessToolForm/Sections/FinancialPlan/components/FinancialPlanChipSelector';
import {
  ActiveTabContextProvider,
  useTabChangeContext,
} from 'components/BusinessToolForm/Sections/FinancialPlan/hooks/useTabChangeContext';
import styles from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/ProjectedCashFlow.module.scss';
import {
  IProjectedCashFlowValuesType,
  projectedCashFlowOptionsToComponentMap,
  projectedCashFlowPrefix,
  useGetInputFieldsList,
  useGetProjectedCashFlowOptions,
} from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/utils/form';

import { concatWithDot } from 'helpers/string';

const ProjectedCashFlowContent: React.FC = () => {
  const { t } = useTranslation();
  const PROJECTED_CASH_FLOW_OPTIONS = useGetProjectedCashFlowOptions();
  const INPUT_FIELDS_LIST = useGetInputFieldsList();
  const tabsForInvestors = PROJECTED_CASH_FLOW_OPTIONS.slice(0, 1);

  const { activeTab, onTabChange } = useTabChangeContext();

  const { control } = useFormContext();

  const canAccess = useCheckSubSectionAccess(
    FOR_INVESTORS_SECTIONS.FINANCIAL_PLAN,
    BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.PROJECTED_CASH_FLOW,
  );

  const Component = projectedCashFlowOptionsToComponentMap[activeTab.value as IProjectedCashFlowValuesType];

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE} disabled={!canAccess}>
        <FormHeader
          locked={!canAccess}
          title={t('financialPlan.title')}
          badgeText={t('financialPlan.projectedCashFlow.title')}
        />
      </FormSectionCard>

      <FormSectionCard collapsible title={t('financialPlan.projectedCashFlow.cashFlows')} disabled={!canAccess}>
        <Box className={styles.contentWrapper}>
          <FinancialPlanChipSelector
            wrapWithSectionCard={false}
            options={tabsForInvestors}
            selected={activeTab}
            onChange={onTabChange}
          />

          <Component />
        </Box>
      </FormSectionCard>

      {INPUT_FIELDS_LIST.map(({ title, name }) => {
        return (
          <FormSectionCard
            disabled={!canAccess}
            title={title}
            key={title}
            aiFieldName={concatWithDot(projectedCashFlowPrefix, name)}
          >
            <TextInputControl
              name={concatWithDot(projectedCashFlowPrefix, name)}
              control={control}
              multiline
              minRows={3}
              maxLength={1500}
              disabled={!canAccess}
            />
          </FormSectionCard>
        );
      })}
    </>
  );
};

export const ProjectedCashFlow: React.FC = () => {
  const PROJECTED_CASH_FLOW_OPTIONS = useGetProjectedCashFlowOptions();
  return (
    <ActiveTabContextProvider initialValue={PROJECTED_CASH_FLOW_OPTIONS[0]}>
      <ProjectedCashFlowContent />
    </ActiveTabContextProvider>
  );
};
